import { LABEL_STYLE } from "./constants";
import { ScoreBar } from "./ScoreBar";

// PA Score card. Renders whatever the backend returned -- no math is
// performed here. Preserves the color palette, insufficient-data
// branch, market-renormalization note, and disclaimer copy verbatim.
export function PaScoreCard({ paScore }) {
  if (!paScore) return null;
  const {
    pa_score: score,
    pa_label: label,
    historical_value_score: hv,
    recent_form_score: rf,
    market_price_score: mp,
    confidence,
    insufficient_data: insufficient,
  } = paScore;

  const style = LABEL_STYLE[label] || LABEL_STYLE.Weak;

  return (
    <section
      data-testid="analyzer-pa-score"
      className="pa-card p-6 md:p-8 mt-6 md:mt-8"
    >
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <h3 className="font-display text-xl md:text-2xl uppercase tracking-wide text-slate-900">
          PA Score
        </h3>
        <p
          className="text-xs text-slate-500 font-mono-num"
          data-testid="analyzer-pa-score-confidence"
        >
          Data Confidence:{" "}
          <span className="font-semibold text-slate-700">
            {confidence || "Low"}
          </span>
        </p>
      </div>

      <div className="mt-5 flex items-center gap-5 flex-wrap">
        <div
          className="rounded-full flex items-center justify-center"
          style={{
            width: 92,
            height: 92,
            backgroundColor: style.bg,
            boxShadow: `inset 0 0 0 3px ${style.ring}`,
            color: style.text,
          }}
          data-testid="analyzer-pa-score-badge"
        >
          <div className="text-center leading-tight">
            <div
              className="text-3xl font-mono-num font-bold"
              data-testid="analyzer-pa-score-value"
            >
              {insufficient ? "—" : score}
            </div>
            <div className="text-[10px] uppercase tracking-wider opacity-90">
              /100
            </div>
          </div>
        </div>
        <div>
          <p
            className="text-lg font-display uppercase tracking-wide text-slate-900"
            data-testid="analyzer-pa-score-label"
          >
            {label}
          </p>
          {insufficient && (
            <p
              className="mt-1 text-xs text-slate-600"
              data-testid="analyzer-pa-score-insufficient"
            >
              Not enough data to produce a numerical PA Score.
            </p>
          )}
        </div>
      </div>

      <dl className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-4">
        <ScoreBar
          label="Historical Value"
          value={hv}
          max={55}
          testid="analyzer-pa-score-historical"
        />
        <ScoreBar
          label="Recent Form"
          value={rf}
          max={20}
          testid="analyzer-pa-score-recent"
        />
        <ScoreBar
          label="Market Price"
          value={mp}
          max={25}
          testid="analyzer-pa-score-market"
          unavailable={mp === null && !insufficient}
        />
      </dl>

      {mp === null && !insufficient && (
        <p
          className="mt-4 text-xs"
          style={{ color: "#b45309" }}
          data-testid="analyzer-pa-score-market-note"
        >
          Market price component not scored — the exact line isn&apos;t quoted in
          today&apos;s cache. Historical + Recent Form were normalized to 100.
        </p>
      )}

      <p
        className="mt-5 text-xs text-slate-500 border-t border-slate-200 pt-4"
        data-testid="analyzer-pa-score-disclaimer"
      >
        PA Score is a research grade based on historical performance and
        current market pricing. It is not a predicted win probability.
      </p>
    </section>
  );
}
